<template>
    <section class="step-content-container flex-level jc-center wp-10">
        <div class="wp-5 p-8 acenter" style="max-width: 640px">
            <onboarding-trial-illustration
                :lang="currentInterfaceLangId"
            ></onboarding-trial-illustration>
        </div>

        <div class="wp-5 p-8 bl-1 flex-column" style="max-width: 640px">
            <!-- title -->
            <div class="header">
                <h2 class="m-0">{{ $l10n.__('Parlez nous de vous') }}</h2>
                <p class="fs-3 gray-60 m-0 mb-3 semibold">
                    {{ $l10n.__('Editez votre profil Sellsy') }}
                </p>
            </div>

            <!-- form -->
            <el-form
                class="form onboardingFormGTM"
                ref="form"
                label-position="top"
                :model="form"
                :rules="rules"
            >
                <el-form-item
                    v-if="errors.get('login')"
                    :label="
                        $t.__('pages.onboarding.trial.OnboardingTrialStep2.previous_step_error')
                    "
                    :error="errors.get('login')"
                ></el-form-item>
                <el-form-item
                    :label="$t.__('common.labels.your_company_name')"
                    prop="name"
                    :error="errors.get('name')"
                >
                    <text-input
                        mode="edit"
                        :value="form.name"
                        @input="updateName"
                        :placeholder="$t.__('pages.onboarding.trial.OnboardingTrialStep2.company')"
                    />
                </el-form-item>
                <el-form-item
                    :label="$t.__('common.labels.first_name')"
                    prop="firstname"
                    :error="errors.get('firstname')"
                >
                    <text-input
                        mode="edit"
                        :value="form.firstname"
                        @input="updateFirstname"
                        :placeholder="$t.__('common.labels.first_name')"
                    />
                </el-form-item>
                <el-form-item
                    :label="$t.__('common.labels.name')"
                    prop="lastname"
                    :error="errors.get('lastname')"
                >
                    <text-input
                        mode="edit"
                        :value="form.lastname"
                        @input="updateLastname"
                        :placeholder="$t.__('common.labels.name')"
                    />
                </el-form-item>
                <el-form-item
                    :label="$t.__('pages.onboarding.trial.OnboardingTrialStep2.professional_phone')"
                    prop="phone_number"
                    :error="errors.get('phone_number')"
                >
                    <tel-input
                        placeholder=""
                        :value="form.phone_number"
                        @onInput="updatePhone"
                        @onValidate="onValidateTelInput"
                        @onBlur="onBlurTelInput"
                        :default-country="form.phone_country"
                    ></tel-input>
                </el-form-item>
                <p class="semibold fs-3 pt-3 mt-0">{{ $l10n.__('Préférence du compte') }}</p>
                <el-form-item
                    :label="$t.__('pages.onboarding.trial.OnboardingTrialStep2.select_country')"
                    prop="country"
                    :error="errors.get('country')"
                >
                    <country-input
                        mode="edit"
                        :defaultValue="{ code: currentLang }"
                        @change="updateCountry"
                    />
                </el-form-item>
                <el-form-item
                    :label="$t.__('pages.onboarding.trial.OnboardingTrialStep2.language')"
                    prop="lang"
                    :error="errors.get('lang')"
                >
                    <el-select :value="form.lang" @input="updateLang">
                        <template slot="prefix"
                            ><img
                                class="prefix"
                                :src="`/ressources/public/img/flags/lang/${form.lang}.png`"
                        /></template>
                        <el-option
                            v-for="item in availableInterfaceLanguages"
                            :key="item.id"
                            :label="item.label"
                            :value="item.id"
                        >
                            <div class="flex flex-level">
                                <img
                                    :src="`/ressources/public/img/flags/lang/${item.id}.png`"
                                    class="pr-2"
                                />
                                <span>{{ item.label }}</span>
                            </div>
                        </el-option>
                    </el-select>
                </el-form-item>
            </el-form>

            <!-- call to action -->
            <div class="action self-center">
                <CompassButton
                    :label="$t.__('pages.onboarding.trial.OnboardingTrialStep2.save_and_continue')"
                    type="primary"
                    class="self-center"
                    :disabled="isDisabledNextStep"
                    :loading="loading"
                    @click="submitForm('form', 'goToNextStep')"
                />
            </div>
        </div>
    </section>
</template>

<script>
import StepperMixin from 'mixins/StepperMixins';
import FormMixins from 'mixins/FormMixins';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import TelInput from 'vue-tel-input';
import CountryInput from 'vcomponents/inputs/CountryInput.vue';
import LangSelector from 'vcomponents/utilities/LangSelector.vue';
import { createNamespacedHelpers } from 'vuex';
import OnboardingTrialApi from 'api/onboarding/trial/onboardingTrial.js';
import OnboardingTrialRules from 'vcomponents/forms/rules/OnboardingTrialRules';
import OnboardingTrialIllustration from 'vcomponents/pages/onboarding/trial/OnboardingTrialIllustration.vue';

const namespace = 'onboardingTrial';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
const { mapGetters, mapActions } = createNamespacedHelpers(namespace);

const finalizeStatus = {
    SUCCESS: 'success',
    WARNING: 'alreadyExist',
    ERROR: 'error',
};

export default {
    name: 'OnboardingTrialStep2',
    mixins: [StepperMixin, FormMixins],
    data() {
        return {
            loading: false,
            namespaceLocalStorage: namespace,
            pathImage: '/ressources/public/img/onboarding/Sellsy_produit_onboarding3.png',
            rules: OnboardingTrialRules,
            isValidPhone: false,
            isActiveAccountTimer: null,
        };
    },
    mounted() {
        this.initStep();
        // add setTimeout to launch prefillFormWithQueryParams after checking if hasAlreadyValues
        // this for not activate form errors the first time you display step 3 on redirect google
        setTimeout(() => this.prefillFormWithQueryParams());
    },
    computed: {
        ...mapStateSystem(['availableLanguages']),
        ...mapGetters({
            form: 'formStep2',
            finalForm: 'finalForm',
        }),
        /**
         * Return a list of available language for interface
         */
        availableInterfaceLanguages() {
            const idsAILanguages = ['fr', 'en'];
            return this.availableLanguages.filter(({ id }) => idsAILanguages.includes(id));
        },
        /**
         * Returns the id of the interface language passed in the request params, by default "en"
         */
        currentInterfaceLangId() {
            return (
                this.availableInterfaceLanguages.find(({ id }) => id === this.currentLang)?.id ||
                'en'
            );
        },
    },
    methods: {
        ...mapActions([
            'initialiseStore',
            'updateLogin',
            'updateName',
            'updateFirstname',
            'updateLastname',
            'updatePhone',
            'updateCountry',
            'updateLang',
            'updateRecaptcha',
            'updateGoogle',
            'updatePassword',
        ]),
        /**
         * Go to the next step
         */
        async goToNextStep() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.persistStateInLocalStorage(this.finalForm);

            const token = await this.getTokenReCaptchaV3('onboardingTrialValidate');
            this.updateRecaptcha(token);

            OnboardingTrialApi.validateStep(this.finalForm, 'account').then(
                (response) => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'gtm.formsubmit',
                        formId: this.finalForm,
                    });
                    this.finalizeOnboarding(response);
                },
                (error) => {
                    this.loading = false;
                    Push.error(
                        this.$l10n.__("Une erreur est survenue lors de l'enregistrement"),
                        false,
                    );
                    this.errors.setErrors(error);
                },
            );
        },
        /**
         * According to the status, redirect to the good route
         *
         * @param {Object} response - success response of onboarding
         */
        finalizeOnboarding(response) {
            switch (response.status) {
                // launch an intervall of calls until the account is activate
                // then redirect to session
                case finalizeStatus.SUCCESS:
                    this.$router.push({
                        name: 'onboarding.trial.final',
                        params: {
                            response,
                            title: this.$l10n.__('Confirmation de votre compte Sellsy'),
                            description: this.$l10n.__('cela peut prendre quelques instants.'),
                        },
                    });
                    break;
                // redirect to final view with appropriate message
                case finalizeStatus.WARNING:
                    this.$router.push({
                        name: 'onboarding.trial.final',
                        params: {
                            response,
                            title: this.$l10n.__(
                                'Votre demande a été envoyée à notre équipe commerciale',
                            ),
                            description: this.$l10n.__(
                                'Nous avons bien reçu votre demande, un commercial vous recontactera dans les plus brefs délais.',
                            ),
                        },
                    });
                    break;
                // redirect to final view with appropriate message
                case finalizeStatus.ERROR:
                    this.$router.push({
                        name: 'onboarding.trial.final',
                        params: {
                            response,
                            title: this.$l10n.__(
                                'Une erreur est survenue lors de la création de votre compte',
                            ),
                            description: `
                            <p class='mb-2'>${this.$l10n.__(
                                'Notre équipe a été prévenu de ce problème. Nous vous invitons à relancer la création de votre compte d’ici quelques minutes.',
                            )}</p>
                            <p class='m-0'>${this.$l10n.__(
                                'Si le problème persiste, vous pouvez nous contacter sur l’adresse email : contact@sellsy.com',
                            )}</p>
                        `,
                        },
                    });
                    break;
            }
        },
        /**
         * on validate event of tel input,
         * if is not valid value set field to error
         * else set field to success
         */
        onValidateTelInput({ isValid }) {
            this.isValidPhone = isValid;

            const phoneNumber = this.$refs.form.fields.find(({ prop }) => prop === 'phone_number');

            if (phoneNumber) {
                if (!isValid) {
                    phoneNumber.validateState = 'error';
                    phoneNumber.validateMessage = this.rules.phone_number[0].message;
                } else {
                    phoneNumber.validateState = 'success';
                    phoneNumber.validateMessage = undefined;
                }
            }
        },
        /**
         * On blur event of tel input,
         * if is not valid phone call onValidateTelInput
         * (examle case : user focus input and blur without value or with wrong value)
         */
        onBlurTelInput() {
            if (!this.isValidPhone) this.onValidateTelInput({ isValid: this.isValidPhone });
        },
        /**
         * Get only the query params we want and prefill form with them
         * Then persist state in local storage
         */
        prefillFormWithQueryParams() {
            const { email: Login, first_name: Firstname, last_name: Lastname } = this.$route.query;
            const Lang = this.currentInterfaceLangId;
            const Country = { code: this.currentLang.toUpperCase() };

            for (const [field, value] of Object.entries({
                Login,
                Firstname,
                Lastname,
                Lang,
                Country,
            })) {
                if (value) this[`update${field}`](value);
            }

            if (Login && Firstname && Lastname) {
                this.updatePassword(null);
                this.updateGoogle(true);
            } else {
                this.updateGoogle(false);
            }

            this.persistStateInLocalStorage(this.finalForm);
        },
    },
    beforeDestroy() {
        clearInterval(this.isActiveAccountTimer);
    },
    components: {
        TextInput,
        TelInput,
        CountryInput,
        LangSelector,
        OnboardingTrialIllustration,
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

.step-content-container {
    height: 528px;
    margin-top: auto;
    margin-bottom: auto;
}

.el-form-item {
    margin-bottom: spacer(2);
}
.prefix {
    margin-top: spacer(2);
}
.vue-tel-input:focus-within {
    border-color: $primary-50 !important;
    box-shadow: none !important;
}
.el-form-item.is-error .vue-tel-input,
.el-form-item.is-error .vue-tel-input:focus-within {
    border-color: $red !important;
}
</style>
